import React, { useState } from 'react';
import { Star, Trash2, Pencil, ChevronUp, ChevronDown, Sparkles, DollarSign } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Reward as RewardType, calculateRewardValue } from '@/utils/storage';
import { Card, CardContent } from '@/components/ui/card';
import { ClaimRewardDialog, SpendMoneyDialog } from '../dialogs';

interface RewardProps {
  reward: RewardType;
  availableStars: number;
  totalStarsEarned?: number;
  onClaim: (reward: RewardType) => void;
  onSpendMoney?: (reward: RewardType, amount: number) => void;
  onEdit?: (reward: RewardType) => void;
  onDelete?: (rewardId: string) => void;
  onMoveUp?: (rewardId: string) => void;
  onMoveDown?: (rewardId: string) => void;
  editMode?: boolean;
}

const Reward: React.FC<RewardProps> = ({
  reward,
  availableStars,
  totalStarsEarned = 0,
  onClaim,
  onSpendMoney,
  onEdit,
  onDelete,
  onMoveUp,
  onMoveDown,
  editMode = false
}) => {
  const [showClaimDialog, setShowClaimDialog] = useState(false);
  const [showSpendMoneyDialog, setShowSpendMoneyDialog] = useState(false);
  
  // Handle calculated vs fixed rewards
  const isCalculatedReward = reward.type === 'calculated';
  const calculatedValue = isCalculatedReward ? calculateRewardValue(reward, totalStarsEarned) : 0;
  
  // Determine if this is a monetary reward (either calculated or fixed with currency)
  const isMonetaryReward = !!reward.currency;
  
  // For fixed rewards: check if affordable
  // For calculated rewards: check if there's value to claim
  const isAffordable = isCalculatedReward ? calculatedValue > 0 : availableStars >= reward.cost;
  const progressPercentage = isCalculatedReward 
    ? Math.min(100, ((totalStarsEarned % (reward.threshold || 1)) / (reward.threshold || 1)) * 100)
    : Math.min(100, (availableStars / reward.cost) * 100);
  
  const handleClick = () => {
    if (isAffordable && !editMode) {
      if (isMonetaryReward) {
        setShowSpendMoneyDialog(true);
      } else {
        setShowClaimDialog(true);
      }
    }
  };

  const handleConfirmClaim = () => {
    onClaim(reward);
    setShowClaimDialog(false);
  };

  const handleConfirmSpendMoney = (amount: number) => {
    if (onSpendMoney) {
      onSpendMoney(reward, amount);
    }
    setShowSpendMoneyDialog(false);
  };
  
  const renderStars = () => {
    if (isMonetaryReward) {
      // For calculated rewards, show total available
      if (isCalculatedReward) {
        return (
          <div className="flex items-center space-x-1">
            <DollarSign 
              size={28} 
              className={cn(
                "text-green-500 fill-green-500",
                !isAffordable && "text-gray-300 fill-gray-300"
              )} 
            />
            <div className="flex flex-col items-end">
              <span className="text-xs text-muted-foreground">
                Total available:
              </span>
              <span className={cn(
                "text-lg font-medium text-foreground",
                !isAffordable && "text-gray-400"
              )}>
                {calculatedValue > 0 ? `${reward.currency || '$'}${calculatedValue}` : 'Pending'}
              </span>
            </div>
          </div>
        );
      } else {
        // For fixed monetary rewards, show currency and cost
        return (
          <div className="flex items-center space-x-1">
            <DollarSign 
              size={28} 
              className={cn(
                "text-green-500 fill-green-500",
                !isAffordable && "text-gray-300 fill-gray-300"
              )} 
            />
            <span className={cn(
              "text-lg font-medium text-foreground",
              !isAffordable && "text-gray-400"
            )}>
              {reward.currency || '$'}{reward.cost}
            </span>
          </div>
        );
      }
    }
    
    // For star-based rewards
    return (
      <div className="flex items-center space-x-1">
        <Star 
          size={28} 
          className={cn(
            "text-yellow-400 fill-yellow-400",
            !isAffordable && "text-gray-300 fill-gray-300"
          )} 
        />
        <span className={cn(
          "text-lg font-medium text-foreground",
          !isAffordable && "text-gray-400"
        )}>
          {reward.cost}
        </span>
      </div>
    );
  };
  
  return (
    <>
      <Card 
        id={`reward-${reward.id}`}
        className={cn(
          "relative transition-all duration-300 border-border/50 rounded-lg overflow-hidden",
          isAffordable && !editMode ? "cursor-pointer hover:ring-2 hover:ring-primary/50" : "cursor-default",
          !isAffordable && !editMode ? "opacity-80" : ""
        )}
        onClick={editMode ? undefined : handleClick}
      >
        <CardContent className="p-4 rounded-b-lg">
          <div className="flex items-center justify-between">
            <div className="flex flex-1 gap-2">
              <div>
                <h3 className="font-medium text-lg">
                  {reward.title}
                </h3>
                {reward.description && (
                  <p className="text-sm text-muted-foreground">
                    {reward.description}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center space-x-2">
              {renderStars()}
              
              {editMode && (
                <>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onEdit) onEdit(reward);
                    }}
                    className="p-1 rounded-full hover:bg-primary/10"
                  >
                    <Pencil size={18} className="text-primary" />
                  </button>
                  <button 
                    onClick={(e) => {
                      e.stopPropagation();
                      if (onDelete) onDelete(reward.id);
                    }}
                    className="p-1 rounded-full hover:bg-red-100 dark:hover:bg-red-900/50 text-red-500"
                  >
                    <Trash2 size={18} />
                  </button>
                </>
              )}
            </div>
          </div>
          
          {editMode && (
            <div className="flex justify-between mt-3">
              <div className="flex space-x-2">
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onMoveUp) onMoveUp(reward.id);
                  }}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Move reward up"
                >
                  <ChevronUp size={18} />
                </button>
                <button 
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onMoveDown) onMoveDown(reward.id);
                  }}
                  className="p-1.5 rounded-full hover:bg-gray-100 dark:hover:bg-gray-700"
                  aria-label="Move reward down"
                >
                  <ChevronDown size={18} />
                </button>
              </div>
            </div>
          )}
          
          {!isAffordable && !editMode && (
            <div className="absolute bottom-0 left-0 w-full h-1.5 bg-gray-100 rounded-b-lg overflow-hidden">
              {/* Enhanced magical progress bar with multiple shimmer effects */}
              <div 
                className="h-full bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500 rounded-bl-lg relative overflow-hidden transition-all duration-500 ease-out"
                style={{ width: `${progressPercentage}%` }}
              >
                {/* Primary shimmer overlay - much brighter and more visible */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/80 to-transparent animate-shimmer"></div>
                
                {/* Secondary wave shimmer for extra effect */}
                <div className="absolute inset-0 bg-gradient-to-r from-transparent via-yellow-100/90 to-transparent animate-shimmer-wave"></div>
                
                {/* Enhanced glowing effect */}
                <div className="absolute inset-0 bg-yellow-300/60 blur-sm animate-pulse"></div>
                
                {/* Bright highlight line */}
                <div className="absolute top-0 left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-white/90 to-transparent animate-shimmer"></div>
                
                {/* Enhanced sparkle particles with more visibility */}
                {progressPercentage > 0 && (
                  <>
                    <div className="absolute top-0 left-1/4 w-2 h-2 bg-white rounded-full animate-sparkle opacity-0 shadow-lg"></div>
                    <div className="absolute top-0 right-1/3 w-1.5 h-1.5 bg-yellow-100 rounded-full animate-sparkle-delayed opacity-0 shadow-md"></div>
                    <div className="absolute bottom-0 left-2/3 w-2 h-2 bg-white rounded-full animate-sparkle-slow opacity-0 shadow-lg"></div>
                    <div className="absolute center w-1 h-1 bg-white rounded-full animate-sparkle opacity-0 shadow-sm" style={{ left: '40%', top: '25%' }}></div>
                  </>
                )}
              </div>
              
              {/* Floating sparkles around the progress bar */}
              {progressPercentage > 20 && (
                <div className="absolute -top-2 left-0 w-full pointer-events-none">
                  <Sparkles 
                    size={14} 
                    className="absolute text-yellow-400 animate-float drop-shadow-lg" 
                    style={{ left: `${Math.min(progressPercentage - 10, 85)}%` }}
                  />
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <ClaimRewardDialog
        open={showClaimDialog}
        onOpenChange={setShowClaimDialog}
        onConfirm={handleConfirmClaim}
        reward={reward}
      />

      <SpendMoneyDialog
        open={showSpendMoneyDialog}
        onOpenChange={setShowSpendMoneyDialog}
        onConfirm={handleConfirmSpendMoney}
        reward={reward}
        totalStarsEarned={totalStarsEarned}
        availableStars={availableStars}
      />
    </>
  );
};

export default Reward;
